import { AfterViewInit, Component, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output, ViewChild, OnInit, ChangeDetectorRef } from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import moment from 'moment-timezone';
import { Observable ,  Subscription } from 'rxjs';
import { PieChartDataModel } from '../../models/pie-chart-data.model';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { monitoringFiltersEnum } from '../../enums/monitoring-filters.enum';
import { SentimentModel } from '../../models/sentiment.model';
import { MediaTypeModel } from '../../models/media-type.model';
import { FilterTypeCheckboxModel } from '../../../../Common/Filter/models/filter-type-checkbox.model';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { FilterTypeModel } from '../../../../Common/Filter/models/abstract-filter-type.model';
import { MediaTypeTreeFilterModel } from '../../../../Common/Filter/models/media-type-tree-filter.model';
import { FilterOptionModel } from '../../../../Common/Filter/models/filter-option.model';

@Component({
    selector: 'agpr-donut-chart',
    templateUrl: './donut-chart.component.html',
    styleUrls: ['./donut-chart.component.scss'],
    standalone: false
})
export class DonutChartComponent implements AfterViewInit, OnInit, OnDestroy {
    private subscriptions: Subscription[] = [];

    @ViewChild('chart', { static: true }) chartElement: ElementRef;

    @Input() data: Observable<PieChartDataModel[]>;
    @Input() chartName: string;
    @Input() filterType: string;

    @Output() selectedDataItems: EventEmitter<PieChartDataModel>;

    private chart: am4charts.PieChart;
    private series: am4charts.PieSeries;
    public loading: boolean;

    public height: number = 200;

    private filter: FilterTypeModel;

    readonly whitetoolTipLabelColors = [
        '#791b65', '#a641b2', '#197d8c', '#3459d4', '#4b33b6',
        '#b70000', '#cc6200', '#0f5792', '#02882f'
    ];
    readonly blackTooltipLabelColors = [
        '#e65a92', '#c27612', '#ffa000', '#02aeb8', '#1ad6c4',
        '#ffd900', '#0ccbf9', '#0ccbd9', '#beb702', '#b6dc33',
        '#ff866a', '#0cb678', '#fe7585', '#c7c7c7', '#0cbc7b',
        '#ffb300', '#fd3950', '#bec6cf', '#ff6726', '#8976db', '#007bd8'
    ];

    constructor(
        private zone: NgZone,
        private mentionSearchService: MentionSearchService,
        private cdr: ChangeDetectorRef
    ) {
        this.selectedDataItems = new EventEmitter<PieChartDataModel>();
        this.loading = true;
    }

    public ngOnInit(): void {
        this.subscriptions.push(
            this.mentionSearchService.getFilters().subscribe(
                filters => {
                    if (!filters) {
                        return;
                    }

                    this.filter = filters.find(filter => filter.id === this.filterType);

                    if (this.filterType === monitoringFiltersEnum.mediaTypes) {
                        this.filter = this.mentionSearchService.mediaTypes;
                    }

                    if (this.chart && this.chart.series && this.chart.series.values) {
                        this.setFiltered();
                    }
                }
            )
        );
    }

    /**
     * Initialize the chart.
     * NOTE: This must be run outside of Angular zone to work properly.
     */
    private initChart(): am4charts.PieChart {
        am4core.options.commercialLicense = true;
        const chart = am4core.create(this.chartElement.nativeElement, am4charts.PieChart);

        // Create series
        this.series = chart.series.push(new am4charts.PieSeries());
        this.series.dataFields.value = 'value';
        this.series.dataFields.category = 'label';
        this.series.ticks.template.disabled = true;
        this.series.labels.template.disabled = true;
        this.series.slices.template.states.getKey('active').properties.shiftRadius = 0;

        // Put a white border around each Slice
        this.series.slices.template.stroke = am4core.color(chartColors.agprLighterGrey);
        this.series.slices.template.strokeWidth = 2;
        this.series.slices.template.strokeOpacity = 1;

        // Round the corners
        this.series.slices.template.cornerRadius = 3;

        // Lessen the default margin
        this.series.dx = -5;

        chart.innerRadius = am4core.percent(45);

        this.series.slices.template
            // change the cursor on hover to make it apparent the object can be interacted with
            .cursorOverStyle = [
                {
                    'property': 'cursor',
                    'value': 'pointer'
                }
            ];

        // Create a base filter effect (as if it's not there) for the hover to return to
        const shadow = this.series.slices.template.filters.push(new am4core.DropShadowFilter);
        shadow.opacity = 0;

        // Create hover state
        const hoverState = this.series.slices.template.states.getKey('hover');

        // Slightly shift the shadow and make it more prominent on hover
        const hoverShadow = hoverState.filters.push(new am4core.DropShadowFilter);
        hoverShadow.opacity = 0.7;
        hoverShadow.blur = 5;

        // Add a legend
        chart.legend = new am4charts.Legend();
        chart.legend.position = 'right';
        chart.legend.valign = 'top';
        chart.legend.itemContainers.template.paddingTop = 3;
        chart.legend.itemContainers.template.paddingBottom = 3;
        chart.legend.scale = 0.75;
        chart.legend.labels.template.text = '{category} - [font-style:italic]{value}[/], [bold]{value.percent.formatNumber(\'#.#\')}%[/]';
        chart.legend.dx = -30;
        chart.legend.paddingLeft = 0;
        chart.legend.width = 220;

        // LegendSettings changes, null or "" don't clear it, only whitespace does
        this.series.legendSettings.valueText = ' ';
        // Set up filter callbacks if the filter service is set.

        // This event fires when the user clicks on a legend item.
        chart.legend.itemContainers.template.events.on('hit', (ev) => {
            const dataItem = ev.target.dataItem as am4charts.LegendDataItem;
            const legendValue = dataItem.dataContext.dataContext.legendValue;

            // Emit the event via angular so other things can respond to it.
            // If it's left outside of angular then anything in angular that is subscribed will be ignored
            this.zone.run(
                () => {
                    this.toggleFilterForItem(legendValue);
                }
            );
        });

        // This event fires when a user clicks on a slice of the chart.
        this.series.slices.template.events.on('hit', (ev) => {
            const dataItem = ev.target.dataItem as am4charts.LegendDataItem;
            const legendValue = dataItem.dataContext.legendValue;

            // Emit the event via angular so other things can respond to it.
            // If it's left outside of angular then anything in angular that is subscribed will be ignored
            this.zone.run(
                () => {
                    this.filterItemOut(legendValue);
                }
            );
        });

        // Display "{category} - [filtered]" when the given legend item is turned off.
        chart.legend.labels.template.adapter.add('text', (label, target, key) => {
            if (target.dataItem.visible) {
                return '{category} - [font-style:italic]{value}[/], [bold]{value.percent.formatNumber(\'#.#\')}%[/]';
            } else {
                return '{category} - [[filtered]]';
            }
        });

        // Change default marker styling when legend item is turned off.
        chart.legend.markers.template.children.getIndex(0).states.getKey('active').adapter.add('fill', () => {
            return am4core.color('white');
        });

        // Change tooltip style on slice hover
        this.series.slices.template.events.on('over', (ev) => {
            const slice = ev.target;

            const fillColor = slice.fill?.toString?.().toLowerCase();
            slice.tooltip.getFillFromObject = false;
            slice.tooltip.background.fill = slice.fill;

            // Use white label text for dark backgrounds
            // Use dark label text for light backgrounds
            // Otherwise, reset to default
            if (this.whitetoolTipLabelColors.includes(fillColor)) {
                slice.tooltip.label.fill = am4core.color(chartColors.agprWhite);
            } else if (this.blackTooltipLabelColors.includes(fillColor)) {
                slice.tooltip.label.fill = am4core.color(chartColors.agprText);
            } else {
                slice.tooltip.getFillFromObject = true;
            }
        });

        return chart;
    }

    toggleFilterForItem(item: string): void {

        if (this.filterType === monitoringFiltersEnum.sentiment) {
            const filter = this.filter as FilterTypeCheckboxModel;
            const filterValue = filter.getValue();

            if (filterValue.length === 0) {
                this.filterItemOut(item);
                return;
            }

            const index = filterValue.findIndex((val: SentimentModel) => val.id === item);
            if (index >= 0) {
                filterValue.splice(index, 1);
            } else {
                const option = filter.options.find(opt => (opt.value as SentimentModel).id === item);
                filterValue.push(option.value);
                if (filter.options.length === filterValue.length) {
                    filterValue.length = 0;
                }
            }
            filter.setValue(filterValue);
        } else if (this.filterType === monitoringFiltersEnum.mediaTypes) {
            const filter = this.filter as MediaTypeTreeFilterModel;
            const filterValue = filter.getValue();

            if (filterValue.mediaTypes.length === 0) {
                this.filterItemOut(item);
                return;
            }

            const index = filterValue.mediaTypes.findIndex((val: MediaTypeModel) => val.id === item);
            if (index >= 0) {
                filterValue.mediaTypes.splice(index, 1);
            } else {
                const option = filter.options.find(opt => (opt.value as MediaTypeModel).id === item);
                filterValue.mediaTypes.push(option.value);
                if (filter.options.length === filterValue.mediaTypes.length) {
                    filterValue.mediaTypes.length = 0;
                }
            }
            filter.setValue(filterValue);
        }

        this.mentionSearchService.applyFilters();
    }
    filterItemOut(item: string): void {
        let filterValue: (MediaTypeModel | SentimentModel)[];
        let filterOptions: FilterOptionModel<MediaTypeModel | SentimentModel>[];
        if (this.filterType === monitoringFiltersEnum.mediaTypes) {
            const filter = <MediaTypeTreeFilterModel>this.filter;
            filterValue = filter.getValue().mediaTypes;
            filterOptions = filter.options;
        } else {
            const filter = <FilterTypeCheckboxModel>this.filter;
            filterValue = filter.getValue();
            filterOptions = filter.options;
        }

        let newValue = filterValue.slice();
        if (!filterValue || filterValue.length === 0) {
            // add all of the other options, except this one
            const options = filterOptions.filter(
                (filter) => (filter.value as (SentimentModel | MediaTypeModel)).id !== item
            );
            newValue = options.map((filter) => filter.value);
        } else {
            const index = newValue.findIndex((val: SentimentModel | MediaTypeModel) => val.id === item);
            if (index >= 0) {
                newValue.splice(index, 1);
            }
        }

        if (this.filterType === monitoringFiltersEnum.mediaTypes) {
            const filter = <MediaTypeTreeFilterModel>this.filter;
            const actualNewValue = filter.getValue().clone();
            actualNewValue.mediaTypes = newValue as MediaTypeModel[];
            filter.setValue(actualNewValue);
        } else {
            const filter = <FilterTypeCheckboxModel>this.filter;
            filter.setValue(newValue);
        }
        this.mentionSearchService.applyFilters();
    }

    exportData(type: keyof am4core.IExportOptions): void {
        this.chart.exporting.filePrefix = this.chartName + '_' + moment().format('YYYY-MM-DD');

        // turn it off, otherwise, amcharts will call all fontawesome fonts apis and some of them have CORS error
        this.chart.exporting.useWebFonts = false;

        // .export returns a Promise<boolean> but there's not much that can be done with it currently
        this.chart.exporting.export(type);
    }

    setChartColorsFromData(data: PieChartDataModel[]): void {
        const colorList: am4core.Color[] = [];

        data.forEach(
            pieChartDataModel => {
                colorList.push(am4core.color(pieChartDataModel.color));
            }
        );

        this.series.colors.list = colorList;
    }

    ngAfterViewInit() {
        this.data.subscribe(
            data => {
                this.zone.runOutsideAngular(() => {
                    this.loading = data === null;
                    this.cdr.detectChanges();
                    if (this.loading) {
                        return;
                    }

                    // We shouldn't have to do this, for some reason, donut charts throw an exception when disposing in some environments
                    // the chart automatically disposes pieces when the data changes, we can't catch the exception then so we need to
                    // destroy it manually and re-init it everytime the data changes
                    if (this.chart) {
                        try {
                            this.chart.dispose();
                        } catch (e) {
                            console.error(e);
                        }
                    }

                    this.chart = this.initChart();

                    this.setChartColorsFromData(data);
                    this.chart.data = data;
                    this.height = Math.max(
                        200,
                        data.length * 22 + 24
                    );
                    if (data.length > 10) {
                        this.chart.legend.valign = 'top';
                        this.chart.legend.dy = 10;
                    } else {
                        this.chart.legend.valign = 'middle';
                        this.chart.legend.dy = 0;
                    }

                    if (this.filter) {
                        this.setFiltered();
                    }
                    this.cdr.detectChanges();
                });
            });
    }

    private setFiltered() {
        // This has to use requestAnimationFrame because if the visible attribute changes during the animation/rendering stage
        // of the chart, it can sometimes be reset by the rendering process
        requestAnimationFrame(
            () => {
                const series = this.chart.series.values[0].dataItems.values;
                let filterValue = this.filter.getValue();
                if (this.filterType === monitoringFiltersEnum.mediaTypes) {
                    filterValue = (<MediaTypeTreeFilterModel>this.filter).getValue().mediaTypes;
                }
                if (filterValue.length <= 0) {
                    return;
                }
                series.forEach(chartData => {
                    if (chartData) {
                        try {
                            chartData.visible = !!filterValue.find(
                                (filter) => {
                                    let key;
                                    if (this.filterType === monitoringFiltersEnum.sentiment) {
                                        const sentiment = filter as SentimentModel;
                                        key = sentiment.id;
                                    } else if (this.filterType === monitoringFiltersEnum.mediaTypes) {
                                        const mediaType = filter as MediaTypeModel;
                                        key = mediaType.id;
                                    } else {
                                        key = filter;
                                    }
                                    return key === (chartData.dataContext as PieChartDataModel).legendValue;
                                }
                            );
                        } catch (err) {
                            // Sometimes the above code throws a "event no longer available" from amcharts, swallowing the exception
                            // Seems to make it go away, even if you try to log err
                        }
                    }
                });
            }
        );
    }

    ngOnDestroy() {
        this.zone.runOutsideAngular(() => {
            // Disposing the chart occasionally throws an exception, we can't handle the exception any other way
            try {
                if (this.chart) {
                    this.chart.dispose();
                }
            } catch (e) {
                console.error(e);
            }
        });

        this.subscriptions.forEach((sub) => sub.unsubscribe());
        this.subscriptions.length = 0;
    }
}
